import React from 'react';
import { Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { usePermissions } from '../../contexts/PermissionContext';

const roles: Array<{ label: string; value: 'revenda' | 'clientes_plus' | 'producao' | 'vendedor' | 'admin' | 'cliente' }> = [
  { label: 'Admin', value: 'admin' },
  { label: 'Revendedor', value: 'revenda' },
  { label: 'Cliente Plus', value: 'clientes_plus' },
  { label: 'Produção', value: 'producao' },
  { label: 'Vendas', value: 'vendedor' },
  { label: 'Cliente', value: 'cliente' },
];

export function ImpersonationPanel() {
  const { 
    userInfo, 
    baseRole, 
    role, 
    impersonatedRole, 
    setImpersonatedRole,
    isSimulating,
    simulationLoading
  } = usePermissions();
  
  const isAdmin = !!userInfo?.isAdmin || baseRole === 'admin';
  if (!isAdmin) return null;

  return (
    <div className="bg-navy-800 border border-navy-700 rounded p-3 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-white text-sm font-medium">Simular Fluxo</div>
          <div className="text-xs text-gray-400">
            Teste a experiência de outros perfis com permissões reais
          </div>
        </div>
        {impersonatedRole && (
          <button
            className="px-2 py-1 text-xs bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded border border-red-500/30"
            onClick={() => setImpersonatedRole(null)}
          >
            Encerrar Simulação
          </button>
        )}
      </div>

      {/* Banner de simulação ativa */}
      {isSimulating && (
        <div className={`flex items-center gap-2 p-2 rounded border ${
          simulationLoading 
            ? 'bg-blue-500/10 border-blue-500/30' 
            : 'bg-yellow-500/10 border-yellow-500/30'
        }`}>
          {simulationLoading ? (
            <>
              <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
              <span className="text-xs text-blue-400">Carregando permissões reais...</span>
            </>
          ) : (
            <>
              <CheckCircle2 className="w-4 h-4 text-green-400" />
              <span className="text-xs text-green-400">
                Simulando <span className="font-bold text-yellow-400">{role}</span> com permissões reais da API
              </span>
            </>
          )}
        </div>
      )}

      {/* Status atual */}
      <div className="flex items-center gap-2 text-xs">
        <span className="text-gray-400">Papel atual:</span>
        <span className={`px-2 py-0.5 rounded ${
          isSimulating 
            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' 
            : 'bg-gray-500/20 text-gray-300'
        }`}>
          {role}
        </span>
        {isSimulating && (
          <span className="text-gray-500">(você é {baseRole})</span>
        )}
      </div>

      {/* Grade de papéis */}
      <div className="grid grid-cols-3 gap-2">
        {roles.map(r => {
          const isActive = role === r.value;
          const isCurrentSimulation = impersonatedRole === r.value;
          
          return (
            <button
              key={r.value}
              onClick={() => setImpersonatedRole(r.value === baseRole ? null : r.value)}
              disabled={simulationLoading}
              className={`px-2 py-1.5 text-xs rounded border transition-all ${
                isActive 
                  ? 'bg-yellow-400 text-navy-900 border-yellow-500 font-medium' 
                  : 'bg-navy-700 text-gray-200 border-navy-600 hover:bg-navy-600 hover:border-navy-500'
              } ${simulationLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {r.label}
              {r.value === baseRole && !isSimulating && (
                <span className="ml-1 text-[10px] opacity-60">(você)</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Aviso */}
      <div className="flex items-start gap-2 p-2 bg-navy-900/50 rounded border border-navy-700">
        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-[10px] text-gray-400 leading-relaxed">
          A simulação usa as permissões <strong>reais</strong> configuradas no Painel Administrativo.
          Alterações feitas no painel são refletidas em até 30 segundos.
        </p>
      </div>
    </div>
  );
}


